<template>
  <div>
    <b-modal
      v-model="visivel"
      title="Selecionar Template"
      size="lg"
      centered
      scrollable
      body-class="modal-templates-body"
      header-class="modal-templates-header"
      footer-class="modal-templates-footer"
      :header-bg-variant="headerVariant"
    >

      <p class="text-muted small mb-4 text-center">
        Clique em um template abaixo para selecioná-lo.
      </p>

      <!-- Loading -->
      <div
        v-if="carregando"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
        />
        <p class="mt-2 text-muted">
          Carregando templates...
        </p>
      </div>

      <!-- Estado vazio -->
      <div
        v-else-if="!templates || templates.length === 0"
        class="text-center py-5"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="64"
          class="text-warning mb-3"
        />
        <h5 class="text-muted">
          Nenhum template aprovado disponível
        </h5>
        <p class="text-muted small">
          <slot name="empty-text">
            Crie templates e aguarde a aprovação do WhatsApp para utilizá-los.
          </slot>
        </p>
      </div>

      <!-- Lista de templates em Grid -->
      <div
        v-else
        class="templates-grid"
      >
        <div
          v-for="template in templates"
          :key="getTemplateKey(template)"
          class="template-card"
          :class="{
            'template-selected': isSelected(template)
          }"
          @click="selecionarTemplate(template)"
        >
          <!-- Card Header -->
          <div class="template-card-header">
            <div class="template-icon">
              <feather-icon
                :icon="isSelected(template) ? 'CheckCircleIcon' : 'FileTextIcon'"
                size="20"
                :class="isSelected(template) ? 'text-success' : 'text-primary'"
              />
            </div>
            <div class="template-name">
              {{ getTemplateName(template) }}
            </div>
          </div>

          <!-- Card Body -->
          <div class="template-card-body">
            <div class="template-preview">
              {{ getTemplatePreview(template) || 'Sem conteúdo' }}
            </div>
          </div>

          <!-- Card Footer -->
          <div class="template-card-footer">
            <small class="text-muted">
              <feather-icon
                icon="CheckIcon"
                size="12"
                class="mr-1"
              />
              Aprovado
            </small>

            <!-- Header Type (imagem/vídeo) -->
            <small
              v-if="getHeaderType(template)"
              class="text-muted"
            >
              <feather-icon
                :icon="getHeaderType(template) === 'IMAGE' ? 'ImageIcon' : 'VideoIcon'"
                size="12"
                class="mr-1"
              />
              {{ getHeaderType(template) === 'IMAGE' ? 'Imagem' : 'Vídeo' }}
            </small>

            <!-- Category Badge -->
            <b-badge
              v-if="showCategory && template.category"
              :variant="categoriaBadgeVariant(template.category)"
              class="badge-category"
            >
              {{ template.category }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Footer fixo com botões -->
      <template #modal-footer>
        <div class="modal-actions">
          <b-button
            variant="outline-primary"
            @click="irParaTemplates"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Criar Template
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="cancelarSelecao"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
            />
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            :disabled="!templateSelecionadoObj"
            @click="confirmarSelecao"
          >
            <feather-icon
              icon="CheckIcon"
              size="16"
              class="mr-1"
            />
            Confirmar Seleção
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal, BButton, BSpinner, BAlert, BBadge,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'ModalSelecaoTemplates',
  components: {
    BModal,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
    FeatherIcon,
  },
  props: {
    templates: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Boolean,
      default: false,
    },
    carregando: {
      type: Boolean,
      default: false,
    },
    showCategory: {
      type: Boolean,
      default: false,
    },
    headerVariant: {
      type: String,
      default: null,
    },
    templateKeyField: {
      type: String,
      default: 'name',
    },
  },
  data() {
    return {
      templateSelecionado: null,
      templateSelecionadoObj: null,
    }
  },
  computed: {
    visivel: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },
  watch: {
    visivel(newVal) {
      if (!newVal) {
        this.resetarSelecao()
      }
    },
  },
  methods: {
    getTemplateKey(template) {
      return template.id || template.name || template.template_name_meta
    },
    getTemplateName(template) {
      return template.name || template.template_name_meta
    },
    getTemplatePreview(template) {
      if (template.preview) {
        return template.preview
      }

      if (template.components && Array.isArray(template.components)) {
        const bodyComponent = template.components.find(c => c.type === 'BODY')
        if (bodyComponent && bodyComponent.text) {
          return bodyComponent.text
        }
      }

      if (template.body) {
        return template.body
      }

      if (template.text) {
        return template.text
      }

      return null
    },
    getHeaderType(template) {
      if (template.components && Array.isArray(template.components)) {
        const headerComponent = template.components.find(c => c.type === 'HEADER')
        if (headerComponent && (headerComponent.format === 'IMAGE' || headerComponent.format === 'VIDEO')) {
          return headerComponent.format
        }
      }
      return null
    },
    isSelected(template) {
      const key = this.getTemplateKey(template)
      return this.templateSelecionado === key
    },
    selecionarTemplate(template) {
      this.templateSelecionado = this.getTemplateKey(template)
      this.templateSelecionadoObj = template
    },
    irParaTemplates() {
      this.visivel = false
      this.$router.push({ name: 'templates' })
    },
    categoriaBadgeVariant(categoria) {
      const map = {
        MARKETING: 'primary',
        UTILITY: 'info',
        TRANSACTIONAL: 'success',
      }
      return map[categoria] || 'secondary'
    },
    confirmarSelecao() {
      if (this.templateSelecionadoObj) {
        this.$emit('template-selecionado', {
          name: this.getTemplateName(this.templateSelecionadoObj),
          content: this.getTemplatePreview(this.templateSelecionadoObj),
          template: this.templateSelecionadoObj,
        })

        this.resetarSelecao()
        this.visivel = false
      }
    },
    cancelarSelecao() {
      this.resetarSelecao()
      this.visivel = false
    },
    resetarSelecao() {
      this.templateSelecionado = null
      this.templateSelecionadoObj = null
    },
  },
}
</script>

<style scoped>
/* Modal Customization */
::v-deep .modal-templates-header {
  border-bottom: none;
}

::v-deep .modal-templates-header .modal-title {
  color: #707070 !important;
  font-weight: 600;
}

::v-deep .modal-templates-header .close {
  color: #707070 !important;
  opacity: 1;
  text-shadow: none;
  font-weight: 300;
}

::v-deep .modal-templates-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

/* Templates Grid - Layout responsivo */
.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

/* Responsividade mobile */
@media (max-width: 768px) {
  .templates-grid {
    grid-template-columns: 1fr;
  }
}

/* Tablets */
@media (min-width: 769px) and (max-width: 1024px) {
  .templates-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Template Card */
.template-card {
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 200px;
}

.template-card:hover {
  border-color: #7367f0;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.15);
  transform: translateY(-2px);
}

.template-selected {
  border-color: #28c76f;
  background: #f0fff4;
  box-shadow: 0 4px 16px rgba(40, 199, 111, 0.2);
}

/* Card Header */
.template-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.template-selected .template-card-header {
  background: linear-gradient(135deg, #e8f5e9 0%, #f0fff4 100%);
  border-bottom-color: #28c76f;
}

.template-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.template-name {
  flex: 1;
  font-weight: 600;
  font-size: 0.95rem;
  color: #5e5873;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Card Body */
.template-card-body {
  flex: 1;
  padding: 1rem;
  overflow: hidden;
}

.template-preview {
  font-size: 0.85rem;
  line-height: 1.6;
  color: #6e6b7b;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  max-height: 120px;
}

/* Card Footer */
.template-card-footer {
  padding: 0.75rem 1rem;
  background: #fafafa;
  border-top: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.template-selected .template-card-footer {
  background: #f0fff4;
  border-top-color: #d4edda;
}

.badge-category {
  font-size: 0.65rem;
  padding: 2px 6px;
  line-height: 1;
}

/* Modal Footer fixo */
::v-deep .modal-templates-footer {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 2px solid #f0f0f0;
  padding: 1rem 1.5rem;
  z-index: 10;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
}

.modal-actions button {
  display: flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
}

/* Scrollbar Customization */
::v-deep .modal-templates-body::-webkit-scrollbar {
  width: 8px;
}

::v-deep .modal-templates-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::v-deep .modal-templates-body::-webkit-scrollbar-thumb {
  background: #7367f0;
  border-radius: 4px;
}

::v-deep .modal-templates-body::-webkit-scrollbar-thumb:hover {
  background: #5e50ee;
}

/* Responsive */
@media (max-width: 768px) {
  .templates-grid {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-actions button {
    width: 100%;
    justify-content: center;
  }
}
</style>
